// deps
import { useRef, useContext, useCallback, useState } from "react";

// internal-deps
import {
    CheckoutActionTypes,
    CheckoutContext,
    GroupOrderCartContext,
    GroupCartActionTypes,
} from "@swiggy-private/checkout-core";
import { GetGroupCartOrderResponse, NativeLocalCart } from "@swiggy-private/cart-types";
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { useEffectOnce, useMountedRef } from "@swiggy-private/core-ui";

// apis
import { groupCartUnlockApi } from "@cart-client/apis/GroupCartOrder";

// contexts
import { ApiResponse } from "@cart-client/interfaces/Api";

interface IReturnType {
    showUnlockToast: boolean;
    getUnlockedCartData: (hostId: string, localCart: NativeLocalCart) => void;
    onDismissUnlockToast: VoidFunction;
}

const useUnlockGroupCart = (
    hostUserId: string,
    groupCartId: string,
    enablePolling: (resumeWithDelay?: boolean) => void,
    disablePolling: VoidFunction,
): IReturnType => {
    const apiCallRef = useRef(false);
    const { setState } = useContext(GroupOrderCartContext);
    const isMountedRef = useMountedRef();
    const [showUnlockToast, setShowUnlockToast] = useState(false);

    const onDismissUnlockToast = useCallback(() => {
        setShowUnlockToast(false);
    }, []);

    const { checkoutState } = useContext(CheckoutContext);

    const unlockGroupCartFlow = useCallback(
        (
            hostId: string,
            localCart: NativeLocalCart,
        ): Promise<Result<GetGroupCartOrderResponse["data"], Error | ApiResponse>> => {
            return groupCartUnlockApi(groupCartId, hostId, localCart)
                .then(response => {
                    apiCallRef.current = false;
                    return Ok(response);
                })
                .catch(err => {
                    apiCallRef.current = false;
                    return Err(err);
                });
        },
        [groupCartId],
    );

    const getUnlockedCartData = useCallback(
        async (hostId: string, localCart: NativeLocalCart) => {
            try {
                // pause polling
                disablePolling();

                const data = await unlockGroupCartFlow(hostId, localCart);

                if (data.isErr()) {
                    throw data;
                }

                const transitionCartResponse = data.unwrap();

                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        data: transitionCartResponse,
                        isCartLocked: false,
                    },
                });
                setShowUnlockToast(true);

                // re-enable back polling with a delay of 3s, since latest data was already synced with unlock-cart call
                enablePolling(true);
            } catch (err) {
                // polling should be disable until successfull unlock is done
                disablePolling();
                setState({
                    type: GroupCartActionTypes.UPDATE_GROUP_CART_STATE,
                    payload: {
                        error: { error: true },
                    },
                });
            }
        },
        [disablePolling, enablePolling, setState, unlockGroupCartFlow],
    );

    useEffectOnce(() => {
        if (
            checkoutState.type != CheckoutActionTypes.UNLOCK_GROUP_CART ||
            apiCallRef.current ||
            !isMountedRef.current
        ) {
            return;
        }

        const { nativeLocalMergedCart } = checkoutState.payload;

        getUnlockedCartData(hostUserId, nativeLocalMergedCart);
        return;
    });

    return {
        showUnlockToast,
        getUnlockedCartData,
        onDismissUnlockToast,
    };
};

export default useUnlockGroupCart;
